import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LogOut,
  Save,
  Eye,
  Home,
  Building2,
  LayoutGrid,
  TrendingUp,
  Mail,
  Video,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import HeroEditor from '../components/admin/HeroEditor';
import VisionEditor from '../components/admin/VisionEditor';
import ArchitectureEditor from '../components/admin/ArchitectureEditor';
import PlanEditor from '../components/admin/PlanEditor';
import RevenueEditor from '../components/admin/RevenueEditor';
import ContactEditor from '../components/admin/ContactEditor';
import VideoEditor from '../components/admin/VideoEditor';
import SettingsEditor from '../components/admin/SettingsEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Changes saved successfully!');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const updateContent = (section, data) => {
    setContent((prev) => ({
      ...prev,
      [section]: data
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Admin Dashboard
              </h1>
              <p className="text-sm text-slate-600">
                Welcome back, {user?.username}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => window.open('/', '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Site
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save All Changes'}
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 lg:grid-cols-8 gap-2 bg-white p-2 rounded-lg mb-8">
            <TabsTrigger value="hero" className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Hero</span>
            </TabsTrigger>
            <TabsTrigger value="vision" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Vision</span>
            </TabsTrigger>
            <TabsTrigger value="architecture" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Properties</span>
            </TabsTrigger>
            <TabsTrigger value="plan" className="flex items-center gap-2">
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Plan</span>
            </TabsTrigger>
            <TabsTrigger value="revenue" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Revenue</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Contact</span>
            </TabsTrigger>
            <TabsTrigger value="video" className="flex items-center gap-2">
              <Video className="w-4 h-4" />
              <span className="hidden sm:inline">Video</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          {content && (
            <>
              <TabsContent value="hero">
                <HeroEditor
                  data={content.hero}
                  onChange={(data) => updateContent('hero', data)}
                  token={token}
                />
              </TabsContent>

              <TabsContent value="vision">
                <VisionEditor
                  data={content.vision}
                  onChange={(data) => updateContent('vision', data)}
                  token={token}
                />
              </TabsContent>

              <TabsContent value="architecture">
                <ArchitectureEditor
                  data={content.architecture}
                  onChange={(data) => updateContent('architecture', data)}
                  token={token}
                />
              </TabsContent>

              <TabsContent value="plan">
                <PlanEditor
                  data={content.plan}
                  onChange={(data) => updateContent('plan', data)}
                  token={token}
                />
              </TabsContent>

              <TabsContent value="revenue">
                <RevenueEditor
                  data={content.revenueModel}
                  onChange={(data) => updateContent('revenueModel', data)}
                />
              </TabsContent>

              <TabsContent value="contact">
                <ContactEditor
                  data={content.contact}
                  footer={content.footer}
                  onContactChange={(data) => updateContent('contact', data)}
                  onFooterChange={(data) => updateContent('footer', data)}
                />
              </TabsContent>

              <TabsContent value="video">
                <VideoEditor
                  data={content.video}
                  onChange={(data) => updateContent('video', data)}
                  token={token}
                />
              </TabsContent>

              <TabsContent value="settings">
                <SettingsEditor token={token} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
